/**
 * ウォーターマーク＆エディション合成コンポーネント (WatermarkCompositor)
 * aidlc-docs/construction/media_export/docs/watermark_compositor_component.md 準拠
 */

import type { CapturedFrame } from './FrameCapturer';

export interface WatermarkConfig {
  /** 透かし適用の有無 (Web無料版: true, アプリ有料版: false) */
  applyWatermark: boolean;
  /** 表示テキスト (省略時は既定の '🍮 PurinTouch') */
  watermarkText?: string;
  /** 配置位置 (既定値: 'bottom-right') */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** 不透明度 (0.0〜1.0, 既定値: 0.85) */
  opacity?: number;
  /** テスト用ヘルパー: 作業用Canvas生成関数 */
  createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;
}

export class WatermarkCompositor {
  private static readonly DEFAULT_TEXT = '🍮 PurinTouch';
  private static readonly BASE_RESOLUTION = 600; // 基準キャンバス寸法
  private static readonly BASE_FONT_SIZE = 13;
  private static readonly BASE_CORNER_RADIUS = 14;
  private static readonly BASE_PAD_X = 12;
  private static readonly BASE_PAD_Y = 6;
  private static readonly BASE_MARGIN = 14;

  /**
   * 収集されたフレーム列に対して透かし合成を適用
   */
  public compositeFrames(
    frames: (CapturedFrame | ImageData)[],
    config: WatermarkConfig
  ): ImageData[] {
    if (frames.length === 0) {
      return [];
    }

    // 1. 有料版バイパス判定 (applyWatermark === false の場合、完全無劣化で生データを返却)
    if (!config.applyWatermark) {
      return frames.map((f) => ('imageData' in f ? f.imageData : f));
    }

    // 2. 無料版: 各フレームに透かしバッジを描画
    return frames.map((frame) => {
      const imgData = 'imageData' in frame ? frame.imageData : frame;
      return this.compositeSingleFrame(imgData, config);
    });
  }

  /**
   * 単一の ImageData フレームに透かしバッジを合成
   */
  public compositeSingleFrame(frame: ImageData, config: WatermarkConfig): ImageData {
    // 透かし無効時はそのままパススルー
    if (!config.applyWatermark) {
      return frame;
    }

    const width = frame.width;
    const height = frame.height;

    // キャンバス生成（DI または DOM）
    let canvas: HTMLCanvasElement | null = null;
    let ctx: CanvasRenderingContext2D | null = null;

    if (config.createCanvasFn) {
      canvas = config.createCanvasFn(width, height);
      ctx = canvas.getContext('2d');
    } else if (typeof document !== 'undefined' && document.createElement) {
      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      ctx = canvas.getContext('2d');
    }

    // 2Dコンテキストが取得できない環境では原画を安全にフォールバック
    if (!canvas || !ctx) {
      return frame;
    }

    // 1. 原画フレームをキャンバスへ描画
    ctx.putImageData(frame, 0, 0);

    // 2. スケーリング比率の計算 (基準サイズ 600px に対する比率)
    const minDim = Math.min(width, height);
    const scale = Math.max(0.6, Math.min(2.5, minDim / WatermarkCompositor.BASE_RESOLUTION));

    const text = config.watermarkText || WatermarkCompositor.DEFAULT_TEXT;
    const fontSize = Math.round(WatermarkCompositor.BASE_FONT_SIZE * scale);
    const padX = Math.round(WatermarkCompositor.BASE_PAD_X * scale);
    const padY = Math.round(WatermarkCompositor.BASE_PAD_Y * scale);
    const radius = Math.round(WatermarkCompositor.BASE_CORNER_RADIUS * scale);
    const margin = Math.round(WatermarkCompositor.BASE_MARGIN * scale);
    const opacity = typeof config.opacity === 'number' ? config.opacity : 0.85;

    ctx.save();
    ctx.globalAlpha = opacity;

    // フォント設定 & テキスト寸法測定
    ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    ctx.textBaseline = 'middle';
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width || fontSize * text.length * 0.6; // フォールバック幅
    const badgeWidth = textWidth + padX * 2;
    const badgeHeight = fontSize + padY * 2;

    // バッジ配置座標の計算
    const position = config.position || 'bottom-right';
    let badgeX = width - badgeWidth - margin;
    let badgeY = height - badgeHeight - margin;

    if (position === 'bottom-left') {
      badgeX = margin;
      badgeY = height - badgeHeight - margin;
    } else if (position === 'top-right') {
      badgeX = width - badgeWidth - margin;
      badgeY = margin;
    } else if (position === 'top-left') {
      badgeX = margin;
      badgeY = margin;
    }

    // 3. 半透明ピルバッジ（背景）の描画
    ctx.fillStyle = 'rgba(45, 52, 54, 0.65)';
    this.drawRoundedRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, radius);
    ctx.fill();

    // 4. 白抜きテキストの描画
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 2 * scale;
    ctx.fillText(text, badgeX + padX, badgeY + badgeHeight / 2);

    ctx.restore();

    // 5. 合成済みフレームを ImageData として抽出
    return ctx.getImageData(0, 0, width, height);
  }

  /**
   * 角丸矩形パスの描画
   */
  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    const clampedR = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + clampedR, y);
    ctx.arcTo(x + w, y, x + w, y + h, clampedR);
    ctx.arcTo(x + w, y + h, x, y + h, clampedR);
    ctx.arcTo(x, y + h, x, y, clampedR);
    ctx.arcTo(x, y, x + w, y, clampedR);
    ctx.closePath();
  }
}
